import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService } from '@/services/settingsService';

export function useSettings(category?: string) {
  return useQuery({
    queryKey: ['admin', 'settings', category],
    queryFn: () => settingsService.getAll(category),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSetting(key: string) {
  return useQuery({
    queryKey: ['admin', 'settings', 'single', key],
    queryFn: () => settingsService.get(key),
    enabled: !!key,
    staleTime: 1000 * 60,
  });
}

export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      value,
      encrypted,
      category,
      description,
    }: {
      key: string;
      value: string | number | boolean;
      encrypted?: boolean;
      category?: string;
      description?: string;
    }) => settingsService.update(key, value, { encrypted, category, description }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

export function useDeleteSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => settingsService.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

export function useInvalidateCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsService.invalidateCache(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });
}

export function useStripeStatus() {
  return useQuery({
    queryKey: ['admin', 'stripe', 'config'],
    queryFn: () => settingsService.getStripeStatus(),
    staleTime: 1000 * 60,
  });
}

export function useUpdateStripeConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: { secretKey?: string; webhookSecret?: string }) =>
      settingsService.updateStripeConfig(config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stripe', 'config'] });
    },
  });
}

// Legacy - kept for compatibility
export function useClearRateLimitCache() {
  return useMutation({
    mutationFn: () => settingsService.clearRateLimitCache(),
  });
}
